import * as XLSX from "xlsx";
import { PROPCODE_KINDS, PROPCODE_REQUIRED_COLUMNS } from "./constants";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** The slice of a mapping the workbook needs; both the API response and the view model fit. */
export type TPropcodeWorkbookRow = {
    kind: string;
    source: string;
    target: string;
};

function withXlsxExtension(filename: string): string {
    return filename.toLowerCase().endsWith(".xlsx") ? filename : `${filename}.xlsx`;
}

/** File-name-safe slug of the deal name for generated files. */
function slug(name: string): string {
    return name.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "deal";
}

/**
 * The upload workbook: one sheet per kind (Investment, Entity, Asset), each with the
 * two upload columns. All three sheets are always written, because the upload requires
 * them, even when one holds no rows.
 */
export function buildPropcodeMappingWorkbook(rows: readonly TPropcodeWorkbookRow[]): XLSX.WorkBook {
    const workbook = XLSX.utils.book_new();
    for (const kind of PROPCODE_KINDS) {
        const aoa: string[][] = [
            [...PROPCODE_REQUIRED_COLUMNS],
            ...rows.filter((r) => r.kind === kind.value).map((r) => [r.source, r.target]),
        ];
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(aoa), kind.sheet);
    }
    return workbook;
}

/** Trigger browser download of the three-sheet upload workbook. */
export function downloadPropcodeMappingWorkbook(
    filename: string,
    rows: readonly TPropcodeWorkbookRow[],
): void {
    XLSX.writeFile(buildPropcodeMappingWorkbook(rows), withXlsxExtension(filename));
}

/**
 * Copied propcode mappings as an .xlsx `File` in the exact upload format, so they run
 * through the same validation as an upload. Null when there is nothing to copy.
 */
export function buildPropcodeMappingFile(
    dealName: string,
    mappings: readonly {
        kind?: string;
        source_property_code: string;
        target_property_code: string;
        is_active: boolean;
    }[],
): File | null {
    const rows = mappings
        .filter((m) => m.is_active)
        .map((m) => ({
            kind: m.kind ?? "",
            source: m.source_property_code,
            target: m.target_property_code,
        }));
    if (rows.length === 0) return null;
    const bytes = XLSX.write(buildPropcodeMappingWorkbook(rows), {
        type: "array",
        bookType: "xlsx",
    }) as ArrayBuffer;
    return new File([bytes], withXlsxExtension(`${slug(dealName)}-propcode-mappings-copied`), {
        type: XLSX_MIME,
    });
}
